import { z } from "zod/v3";

// RFC-213 P0/P1 — agent pre-fill of the deferred onboarding questions.
// Answers are classified per RFC-212's vocabulary: `observed` facts come
// from the connected stack (deterministic), `proposal`s come from the
// public website (LLM extraction) and must be confirmed by the user.
// Blocker-class questions are never pre-filled.

export const OnboardingPrefillSourceSchema = z.enum(["observed", "proposal"]);
export type OnboardingPrefillSource = z.infer<
  typeof OnboardingPrefillSourceSchema
>;

export const OnboardingPrefillFieldSchema = z
  .object({
    /** Wizard screen / onboarding field id (e.g. "teamSize"). */
    id: z.string(),
    value: z.unknown(),
    source: OnboardingPrefillSourceSchema,
    /** Human provenance line, e.g. "133 users in Microsoft Entra ID". */
    detail: z.string(),
  })
  .strict();
export type OnboardingPrefillField = z.infer<
  typeof OnboardingPrefillFieldSchema
>;

export const OnboardingPrefillDTOSchema = z
  .object({
    /**
     * `idle`       a public site is known but has never been read — reading
     *              it is a user-initiated action, so nothing happens until
     *              the user asks (RFC-212 consent posture).
     * `extracting` a scan the user started is still running; client polls.
     * `ready`      all available sources have reported.
     */
    status: z.enum(["ready", "extracting", "idle"]),
    /** The public site used for proposals, null when none could be derived. */
    websiteUrl: z.string().nullable(),
    /**
     * Logo image discovered on the public site (apple-touch-icon or PNG
     * icon), null when none found. Applying it to the organization is an
     * explicit user action — never automatic.
     */
    logoUrl: z.string().nullable(),
    fields: z.array(OnboardingPrefillFieldSchema),
    generatedAt: z.string(),
  })
  .strict();
export type OnboardingPrefillDTO = z.infer<typeof OnboardingPrefillDTOSchema>;
